import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import {
  genreAddedEvent,
  genrePopupOpenedEvent,
  genreRemovedEvent,
  languageAddedEvent,
  popupGenreAddedEvent,
} from 'src/Helpers/TagManager'
import { getUserId } from 'src/Redux/auth-process/userSlice/selectors'
import {
  useGenreAddedMutation,
  useLanguageAddedMutation,
} from 'src/Redux/analyticsApi'
import { CampaignPlatformTypeEnum, CampaignTypeEnum } from 'src/Types/campaigns'
import { getScheduledTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/selectors'
import { useGetSubscriptionQuery } from 'src/Redux/subscription-process/subscriptionApi'
import { getAllCampaignsCount, useMetadataQuery } from 'src/Redux/metadataApi'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
interface GenresAnalytics {
  handleGenreAddedEvent: (params: {
    platformName: string
    genreId: string
    genreName: string
  }) => void

  handleLanguageAddedEvent: (params: {
    platformName: string
    languageId: string
    languageName: string
    isAnyLanguage?: boolean
  }) => void

  handleGenrePopupOpenedEvent: (params: { platformName: string }) => void

  handlePopupGenreAddedEvent: (params: {
    platformName: string
    genreId?: number
    genreName: string
  }) => void

  handlePopupGenreRemovedEvent: (params: {
    platformName: string
    genreName: string
  }) => void
}

export const useGenresAndLanguagesAnalytics = (): GenresAnalytics => {
  const [genreAdded] = useGenreAddedMutation()
  const [languageAdded] = useLanguageAddedMutation()
  const param = useParams()
  const { data: plan } = useGetSubscriptionQuery()
  const campaignId = Number(param.number)
  const userId = useSelector(getUserId)
  const { data } = useMetadataQuery(null)
  const amountOfCampaigns = getAllCampaignsCount(data)
  const { campaignDetails } = useSpotifyCampaignStatus()
  const scheduledCampaign = useSelector(getScheduledTikTokCampaign)

  const handleGenreAddedEvent = (params: {
    platformName: string
    genreId: string
    genreName: string
  }): void => {
    let campaignType: CampaignTypeEnum | string

    switch (params.platformName) {
      case CampaignPlatformTypeEnum.TIKTOK:
        campaignType = scheduledCampaign.type
        break

      case CampaignPlatformTypeEnum.META:
        campaignType = CampaignTypeEnum.BASIC
        break

      case CampaignPlatformTypeEnum.SPOTIFY:
      default:
        campaignType = campaignDetails?.type || CampaignTypeEnum.BASIC
        break
    }

    genreAddedEvent({
      campaignId,
      campaignType,
      platformName: params.platformName,
      genreId: params.genreId,
      genreName: params.genreName,
      userId: String(userId),
      campaignNumber: amountOfCampaigns || 1,
      subscriptionPlan: plan?.package?.name || '',
    })

    void genreAdded({
      campaignId,
      campaignPlatform: params.platformName as CampaignPlatformTypeEnum,
    })
  }

  const handleLanguageAddedEvent = (params: {
    platformName: string
    languageId: string
    languageName: string
    isAnyLanguage?: boolean
  }): void => {
    const campaignType =
      !amountOfCampaigns || amountOfCampaigns === 1
        ? CampaignTypeEnum.FIRST
        : campaignDetails?.type || CampaignTypeEnum.BASIC

    languageAddedEvent({
      campaignId,
      campaignType,
      platformName: params.platformName,
      languageId: params.languageId,
      languageName: params.languageName,
      userId: String(userId),
      languageOptions: params.isAnyLanguage,
    })

    void languageAdded({
      campaignId: Number(campaignDetails?.id) || campaignId,
      campaignPlatform: CampaignPlatformTypeEnum.SPOTIFY,
    })
  }

  const handleGenrePopupOpenedEvent = (params: {
    platformName: string
  }): void => {
    let campaignType: CampaignTypeEnum | string

    switch (params.platformName) {
      case CampaignPlatformTypeEnum.TIKTOK:
        campaignType = scheduledCampaign.type
        break

      case CampaignPlatformTypeEnum.META:
        campaignType = CampaignTypeEnum.BASIC
        break

      case CampaignPlatformTypeEnum.SPOTIFY:
      default:
        campaignType = campaignDetails?.type || CampaignTypeEnum.BASIC
        break
    }

    genrePopupOpenedEvent({
      campaignType,
      campaignId,
      platformName: params.platformName,
      campaignNumber: amountOfCampaigns || 1,
      userId: String(userId),
    })
  }

  const handlePopupGenreAddedEvent = (params: {
    platformName: string
    genreId?: number
    genreName: string
  }): void => {
    let campaignType: CampaignTypeEnum | string

    switch (params.platformName) {
      case CampaignPlatformTypeEnum.TIKTOK:
        campaignType = scheduledCampaign.type
        break

      case CampaignPlatformTypeEnum.META:
        campaignType = CampaignTypeEnum.BASIC

        break

      case CampaignPlatformTypeEnum.SPOTIFY:
      default:
        campaignType = campaignDetails?.type || CampaignTypeEnum.BASIC
        break
    }
    popupGenreAddedEvent({
      campaignType,
      campaignId,
      genreId: params.genreId,
      genreName: params.genreName,
      platformName: params.platformName,
      campaignNumber: amountOfCampaigns || 1,
      userId: String(userId),
    })
  }

  const handlePopupGenreRemovedEvent = (params: {
    platformName: string
    genreId?: number
    genreName: string
  }): void => {
    let campaignType: CampaignTypeEnum | string

    switch (params.platformName) {
      case CampaignPlatformTypeEnum.TIKTOK:
        campaignType = scheduledCampaign.type
        break

      case CampaignPlatformTypeEnum.META:
        campaignType = CampaignTypeEnum.BASIC

        break

      case CampaignPlatformTypeEnum.SPOTIFY:
      default:
        campaignType = campaignDetails?.type || CampaignTypeEnum.BASIC
        break
    }
    genreRemovedEvent({
      campaignType,
      campaignId,
      genreId: params.genreId,
      genreName: params.genreName,
      platformName: params.platformName,
      userId: String(userId),
    })
  }

  return {
    handleGenreAddedEvent,
    handleLanguageAddedEvent,
    handleGenrePopupOpenedEvent,
    handlePopupGenreAddedEvent,
    handlePopupGenreRemovedEvent,
  }
}
